/**
 * <ky-social-reels> — horizontal video carousel. Lazily loads and plays
 * videos only while they're visible inside the scrolling track; keeps a
 * progress bar and prev/next arrows in sync with scroll position.
 */
class KySocialReels extends HTMLElement {
  /** @type {HTMLElement|null} */
  #track = null;

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLVideoElement[]} */
  #videos = [];

  /** @type {IntersectionObserver|null} */
  #observer = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#cards = Array.from(this.querySelectorAll('[data-card]'));
    this.#videos = Array.from(this.querySelectorAll('[data-video]'));

    const prevBtn = this.querySelector('[data-prev]');
    const nextBtn = this.querySelector('[data-next]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;

    prevBtn?.addEventListener('click', () => this.scrollByCard(-1));
    nextBtn?.addEventListener('click', () => this.scrollByCard(1));

    if (this.#track) {
      this.#track.addEventListener('scroll', () => this.updateProgress(), { passive: true });
    }
    window.addEventListener('resize', () => this.updateProgress());
    this.updateProgress();

    this.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const card = this.querySelector(`[data-card][data-block-id="${blockId}"]`);
      card?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reducedMotion && this.#videos.length) {
      this.#setupVideoObserver();
    }
  }

  /**
   * Scrolls the track by one card width in the given direction.
   * @param {1|-1} direction
   */
  scrollByCard(direction) {
    const card = this.#cards[0];
    if (!card || !this.#track) return;
    const trackStyle = window.getComputedStyle(this.#track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
    const amount = (card.getBoundingClientRect().width + gap) * direction;
    this.#track.scrollBy({ left: amount, behavior: 'smooth' });
  }

  /** Syncs the progress bar fill and prev/next disabled state to scroll position. */
  updateProgress() {
    if (!this.#track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const max = scrollWidth - clientWidth;
    const ratio = max > 0 ? scrollLeft / max : 0;
    const clamped = Math.min(Math.max(ratio, 0), 1);

    if (this.progressFill) {
      this.progressFill.style.width = `${clamped * 100}%`;
    }
    if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 1;
    if (this.nextBtn) this.nextBtn.disabled = max <= 0 || scrollLeft >= max - 1;
  }

  /** Lazily swaps `data-src` → `src` and calls `.load()` once, on first intersection. */
  #loadVideo(video) {
    if (video.dataset.loaded) return;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
    video.dataset.loaded = 'true';
  }

  /** Plays videos while they're in/near the scrolling track's viewport, pauses otherwise. */
  #setupVideoObserver() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            this.#loadVideo(video);
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { root: this.#track, rootMargin: '0px 100px', threshold: 0.6 }
    );

    this.#videos.forEach((video) => this.#observer.observe(video));
  }
}

if (!customElements.get('ky-social-reels')) {
  customElements.define('ky-social-reels', KySocialReels);
}
